/**
 * ExportModal — Export flyer to PNG, JPG, or PDF.
 */
import { useState, useRef } from 'react';
import { useAppStore } from '@/store/appStore';
import { useCanvasStore } from '@/store/canvasStore';
import { cn } from '@/lib/utils';
import {
  Download,
  X,
  Image as ImageIcon,
  FileImage,
  FileText,
  Check,
  Loader2,
  Settings2,
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

type ExportFormat = 'png' | 'jpg' | 'pdf';
type ExportResolution = '1x' | '2x' | '4x' | 'print';

interface ExportOptions {
  format: ExportFormat;
  resolution: ExportResolution;
  includeBleed: boolean;
  quality: number;
}

const RESOLUTION_PRESETS: Record<ExportResolution, { label: string; scale: number; description: string }> = {
  '1x': { label: 'Web (1x)', scale: 1, description: '1080x1350 - Social media' },
  '2x': { label: 'Retina (2x)', scale: 2, description: '2160x2700 - High quality web' },
  '4x': { label: '4K (4x)', scale: 4, description: '4320x5400 - Print quality' },
  'print': { label: 'Print (300dpi)', scale: 3.125, description: '3375x4219 - Professional print' },
};

function fillToCssColor(fill: unknown, fallback = 'transparent') {
  if (typeof fill === 'string') return fill;
  if (fill && typeof fill === 'object' && 'type' in fill && 'color' in fill) {
    const value = fill as { type?: unknown; color?: unknown };
    if (value.type === 'solid' && typeof value.color === 'string') return value.color;
  }
  return fallback;
}

export function ExportModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { currentProjectId } = useAppStore();
  const { canvasState } = useCanvasStore();
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [options, setOptions] = useState<ExportOptions>({
    format: 'png',
    resolution: '2x',
    includeBleed: false,
    quality: 0.95,
  });
  const canvasRef = useRef<HTMLDivElement>(null);

  if (!isOpen) return null;

  const handleExport = async () => {
    if (!canvasRef.current) return;
    
    setIsExporting(true);
    setProgress(10);

    try {
      const scale = RESOLUTION_PRESETS[options.resolution].scale;
      const canvas = canvasRef.current;
      
      // Capture canvas
      setProgress(30);
      const renderedCanvas = await html2canvas(canvas, {
        scale: scale,
        useCORS: true,
        allowTaint: true,
        backgroundColor: canvasState.backgroundColor,
        logging: false,
      });

      setProgress(70);

      const width = renderedCanvas.width;
      const height = renderedCanvas.height;
      const filename = `flyer-${currentProjectId || 'export'}-${Date.now()}`;

      if (options.format === 'pdf') {
        // PDF Export
        const pdf = new jsPDF({
          orientation: width > height ? 'landscape' : 'portrait',
          unit: 'px',
          format: [width, height],
        });

        const imgData = renderedCanvas.toDataURL('image/png');
        
        // Add bleed marks if requested
        if (options.includeBleed) {
          drawBleedMarks(pdf, width, height);
        }

        pdf.addImage(imgData, 'PNG', 0, 0, width, height);
        pdf.save(`${filename}.pdf`);
      } else {
        // PNG/JPG Export
        const mimeType = options.format === 'jpg' ? 'image/jpeg' : 'image/png';
        const link = document.createElement('a');
        link.download = `${filename}.${options.format}`;
        link.href = renderedCanvas.toDataURL(mimeType, options.quality);
        link.click();
      }

      setProgress(100);
      setTimeout(() => {
        setIsExporting(false);
        setProgress(0);
        onClose();
      }, 500);
    } catch (error) {
      console.error('Export failed:', error);
      setIsExporting(false);
      setProgress(0);
    }
  };

  const drawBleedMarks = (pdf: jsPDF, width: number, height: number) => {
    const bleedSize = 36; // 0.125 inch at 300dpi
    pdf.setDrawColor(0);
    pdf.setLineWidth(0.5);

    // Corner marks
    const marks = [
      // Top left
      { x: bleedSize, y: bleedSize / 2, w: 0, h: bleedSize / 2 },
      { x: bleedSize / 2, y: bleedSize, w: bleedSize / 2, h: 0 },
      // Top right
      { x: width - bleedSize, y: bleedSize / 2, w: 0, h: bleedSize / 2 },
      { x: width - bleedSize, y: bleedSize, w: bleedSize / 2, h: 0 },
      // Bottom left
      { x: bleedSize, y: height - bleedSize, w: 0, h: bleedSize / 2 },
      { x: bleedSize / 2, y: height - bleedSize, w: bleedSize / 2, h: 0 },
      // Bottom right
      { x: width - bleedSize, y: height - bleedSize, w: 0, h: bleedSize / 2 },
      { x: width - bleedSize, y: height - bleedSize, w: bleedSize / 2, h: 0 },
    ];

    marks.forEach((mark) => {
      pdf.line(mark.x, mark.y, mark.x + mark.w, mark.y + mark.h);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-xl border border-border bg-card p-6 shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-semibold text-lg flex items-center gap-2">
            <Download className="h-5 w-5 text-primary" />
            Export Flyer
          </h2>
          <button
            onClick={onClose}
            disabled={isExporting}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Preview */}
        <div className="mb-6">
          <label className="text-sm font-medium block mb-2">Preview</label>
          <div className="relative aspect-[4/5] max-h-48 bg-muted rounded-lg overflow-hidden border border-border">
            <div
              ref={canvasRef}
              className="absolute inset-0 origin-top-left scale-[0.15]"
              style={{
                width: canvasState.width,
                height: canvasState.height,
                backgroundColor: canvasState.backgroundColor,
              }}
            >
              {canvasState.layers
                .filter((l) => l.visible)
                .sort((a, b) => a.z_index - b.z_index)
                .map((layer) => (
                  <div
                    key={layer.id}
                    style={{
                      position: 'absolute',
                      left: layer.x,
                      top: layer.y,
                      width: layer.width,
                      height: layer.height,
                      transform: `rotate(${layer.rotation}deg) scale(${layer.scaleX}, ${layer.scaleY})`,
                      opacity: layer.opacity,
                      backgroundColor: fillToCssColor(layer.fill),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: layer.text_style?.fontSize,
                      fontFamily: layer.text_style?.fontFamily,
                      color: layer.text_style?.fill,
                    }}
                  >
                    {layer.type === 'text' && (layer.text_content ?? 'Text')}
                    {layer.type === 'image' && layer.asset_id && (
                      <img
                        src={`/api/assets/${layer.asset_id}`}
                        alt={layer.name}
                        className="w-full h-full object-contain"
                      />
                    )}
                  </div>
                ))}
            </div>
            {isExporting && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 text-white animate-spin mx-auto mb-2" />
                  <p className="text-white text-sm font-medium">{progress}%</p>
                </div>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {canvasState.width} × {canvasState.height} px • {canvasState.layers.length} layers
          </p>
        </div>

        {/* Format Selection */}
        <div className="mb-4">
          <label className="text-sm font-medium block mb-2">Format</label>
          <div className="grid grid-cols-3 gap-2">
            {(['png', 'jpg', 'pdf'] as const).map((format) => (
              <button
                key={format}
                onClick={() => setOptions((o) => ({ ...o, format }))}
                disabled={isExporting}
                className={cn(
                  "flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-all",
                  options.format === format
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-border hover:border-muted-foreground/50"
                )}
              >
                {format === 'png' && <ImageIcon className="h-5 w-5" />}
                {format === 'jpg' && <FileImage className="h-5 w-5" />}
                {format === 'pdf' && <FileText className="h-5 w-5" />}
                <span className="text-xs font-medium uppercase">{format}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Resolution */}
        <div className="mb-4">
          <label className="text-sm font-medium block mb-2">Resolution</label>
          <div className="space-y-2">
            {(Object.keys(RESOLUTION_PRESETS) as ExportResolution[]).map((res) => {
              const preset = RESOLUTION_PRESETS[res];
              return (
                <button
                  key={res}
                  onClick={() => setOptions((o) => ({ ...o, resolution: res }))}
                  disabled={isExporting}
                  className={cn(
                    "w-full flex items-center justify-between p-3 rounded-lg border transition-all",
                    options.resolution === res
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/50"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "h-5 w-5 rounded-full border flex items-center justify-center",
                      options.resolution === res
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-muted-foreground/30"
                    )}>
                      {options.resolution === res && <Check className="h-3 w-3" />}
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-sm">{preset.label}</p>
                      <p className="text-xs text-muted-foreground">{preset.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Advanced Options */}
        <div className="mb-6">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Settings2 className="h-4 w-4" />
            Advanced Options
          </button>
          
          {showAdvanced && (
            <div className="mt-3 p-3 rounded-lg bg-muted/50 space-y-3">
              {/* Bleed Marks */}
              {options.format === 'pdf' && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.includeBleed}
                    onChange={(e) => setOptions((o) => ({ ...o, includeBleed: e.target.checked }))}
                    className="rounded border-input"
                  />
                  <span className="text-sm">Include bleed marks (for print)</span>
                </label>
              )}
              
              {/* Quality (JPG only) */}
              {options.format === 'jpg' && (
                <div>
                  <label className="text-sm block mb-1">Quality: {Math.round(options.quality * 100)}%</label>
                  <input
                    type="range"
                    min="0.5"
                    max="1"
                    step="0.05"
                    value={options.quality}
                    onChange={(e) => setOptions((o) => ({ ...o, quality: parseFloat(e.target.value) }))}
                    className="w-full"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isExporting}
            className="px-4 py-2 rounded-lg border border-border text-sm hover:bg-muted transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isExporting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Export {options.format.toUpperCase()}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
